import React from "react";
import {
  Col,
  Card,
  CardHeader,
  Row,
  FormInput,
} from "../../components/ui";
import { getAllEnabledProductsBilling, validateCoupon } from "../../api";
import "../common/PlansCards.scss";

const getMonthsInCycle = (billingCycle) => {
  if (billingCycle === 12 || billingCycle === 365) return 12;
  if (billingCycle === 3 || billingCycle === 92) return 3;
  if (billingCycle === 1 || billingCycle === 30) return 1;
  return null;
};

const isPopularPlan = (plan) => {
  const name = (plan?.name || "").toLowerCase();
  if (name === "annual" || name === "yearly" || name.includes("annual")) {
    return true;
  }
  return plan?.billingCycle === 12 || plan?.billingCycle === 365;
};

const getBadgeVariant = (planName) => {
  const name = (planName || "").toLowerCase();
  if (name === "quarterly") return "accent";
  if (name === "annual" || name === "yearly" || name.includes("annual")) {
    return "value";
  }
  return "neutral";
};

const formatPlanTitle = (name) => {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default class PlansCardsFilterable extends React.Component {
  constructor(props) {
    super(props);
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const couponFromUrl = params.get("coupon");
    this.state = {
      products: [],
      showCouponError: false,
      couponError: "",
      coupon: couponFromUrl || "",
      appliedCoupon: null,
      appliedCoupons: [],
      couponApplying: false,
    };
  }

  componentDidMount = async () => {
    const products = await getAllEnabledProductsBilling();
    const nextProducts = this.props.isEnded
      ? products.data
      : this.filterUpgrade(products.data);

    this.setState({ products: nextProducts }, () => {
      if ((this.state.coupon || "").trim()) {
        this.applyCoupon();
      }
    });
  };

  plan = (billingCycle) => {
    if (billingCycle === 12 || billingCycle === 365) {
      return { name: "Yearly", interval: "per year" };
    } else if (billingCycle === 1 || billingCycle === 30) {
      return { name: "Monthly", interval: "per month" };
    } else if (billingCycle === 3 || billingCycle === 92) {
      return { name: "Quarterly", interval: "per quarter" };
    } else if (billingCycle === 65535) {
      return { name: "Lifetime", interval: "one-time" };
    } else {
      return {};
    }
  };

  filterUpgrade = (products) => {
    let billingCycleForCurrentProduct = products.filter(
      (p) => p.id === this.props.filterForProduct
    )[0].billingCycle;
    return products.filter(
      (p) => p.billingCycle > billingCycleForCurrentProduct
    );
  };

  onCouponInputChange = (event) => {
    this.setState({
      coupon: event.target.value,
      showCouponError: false,
      couponError: "",
    });
  };

  clearCouponInput = () => {
    this.setState({
      coupon: "",
      showCouponError: false,
      couponError: "",
    });
  };

  removeCoupon = () => {
    this.setState({
      coupon: "",
      appliedCoupon: null,
      appliedCoupons: [],
      showCouponError: false,
      couponError: "",
      couponApplying: false,
    });
  };

  changeCouponCode = () => {
    const previousCode = this.state.appliedCoupon || "";
    this.setState({
      appliedCoupon: null,
      appliedCoupons: [],
      coupon: previousCode,
      showCouponError: false,
      couponError: "",
      couponApplying: false,
    });
  };

  applyCoupon = async (e) => {
    e?.preventDefault?.();

    const code = (this.state.coupon || "").trim();
    if (!code || this.state.couponApplying || this.state.appliedCoupon) {
      return;
    }

    if (!this.state.products.length) {
      return;
    }

    this.setState({
      couponApplying: true,
      showCouponError: false,
      couponError: "",
    });

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const productIds = this.state.products.map((product) => product.id);
      const resp = await validateCoupon({
        couponName: code,
        productIds,
        userId: user?.id,
      });

      const results = (resp?.data || []).filter(Boolean);
      const hasApplicable = results.some((item) => item.applicable !== false);

      if (hasApplicable) {
        this.setState({
          coupon: code,
          appliedCoupon: code,
          appliedCoupons: results,
          showCouponError: false,
          couponApplying: false,
        });
      } else {
        this.setState({
          appliedCoupon: null,
          appliedCoupons: [],
          showCouponError: true,
          couponError:
            results[0]?.reason || "This coupon is not valid for these plans.",
          couponApplying: false,
        });
      }
    } catch {
      this.setState({
        appliedCoupon: null,
        appliedCoupons: [],
        showCouponError: true,
        couponError: "Could not validate coupon. Please try again.",
        couponApplying: false,
      });
    }
  };

  hasAppliedCoupon = () => Boolean(this.state.appliedCoupon);

  getCouponForPlan = (planId) => {
    if (!this.state.appliedCoupon) {
      return (this.state.coupon || "").trim();
    }

    const discount = this.state.appliedCoupons.find(
      (item) => item.productId === planId
    );

    if (discount && discount.applicable !== false) {
      return this.state.appliedCoupon;
    }

    return "";
  };

  getDiscountedCostForPlan = (plan) => {
    const discount = this.state.appliedCoupons.find(
      (item) => item.productId === plan.id
    );

    if (discount && discount.applicable !== false && discount.discountedCost != null) {
      return discount.discountedCost;
    }

    return null;
  };

  checkDiscount = (plan) => {
    const discountedPlan = this.state.appliedCoupons.filter(
      (c) => c.productId === plan.id
    );

    if (discountedPlan.length > 0) {
      const discount = discountedPlan[0];

      if (discount.applicable === false) {
        return (
          <>
            <div className="plan-card__price">
              <span className="plan-card__price-currency">$</span>
              <span className="plan-card__price-amount">{plan.cost}</span>
            </div>
            {this.hasAppliedCoupon() && (
              <span className="plan-card__coupon-note">
                Coupon does not apply to this plan
              </span>
            )}
          </>
        );
      }

      return (
        <div className="plan-card__price plan-card__price--discounted">
          <span className="plan-card__price-original">${plan.cost}</span>
          <span className="plan-card__price-currency">$</span>
          <span className="plan-card__price-amount">
            {discount.discountedCost}
          </span>
        </div>
      );
    }

    return (
      <div className="plan-card__price">
        <span className="plan-card__price-currency">$</span>
        <span className="plan-card__price-amount">{plan.cost}</span>
      </div>
    );
  };

  render() {
    const couponTrimmed = (this.state.coupon || "").trim();
    const hasAppliedCoupon = this.hasAppliedCoupon();

    return (
      <>
        <div className="billing-coupon-row">
          <div className="billing-coupon">
            <div className="billing-coupon__head">
              <label htmlFor="billing-coupon-code">
                {hasAppliedCoupon ? "Coupon" : "Have a coupon?"}
              </label>
              {!hasAppliedCoupon && (
                <span className="billing-coupon__hint">One code per order</span>
              )}
            </div>

            {hasAppliedCoupon ? (
              <div className="billing-coupon__applied">
                <div className="billing-coupon__applied-main">
                  <span
                    className="billing-coupon__applied-icon"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                  <div>
                    <p className="billing-coupon__applied-title">
                      Coupon applied
                    </p>
                    <span className="billing-coupon__chip-code">
                      {this.state.appliedCoupon}
                    </span>
                    <span className="billing-coupon__chip-status">
                      Applied to eligible plans
                    </span>
                  </div>
                </div>
                <div className="billing-coupon__applied-actions">
                  <button
                    type="button"
                    className="billing-coupon__change"
                    onClick={this.changeCouponCode}
                  >
                    Change code
                  </button>
                  <button
                    type="button"
                    className="billing-coupon__remove"
                    onClick={this.removeCoupon}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="billing-coupon__controls">
                  <div className="billing-coupon__input-wrap">
                    <FormInput
                      id="billing-coupon-code"
                      className={`billing-coupon__input${
                        this.state.coupon.length > 0
                          ? " billing-coupon__input--has-clear"
                          : ""
                      }`}
                      type="text"
                      placeholder="Enter code"
                      value={this.state.coupon}
                      onChange={this.onCouponInputChange}
                      disabled={this.state.couponApplying}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          this.applyCoupon(e);
                        }
                      }}
                    />
                    {this.state.coupon.length > 0 ? (
                      <button
                        type="button"
                        className="billing-coupon__clear"
                        aria-label="Clear coupon code"
                        onClick={this.clearCouponInput}
                        disabled={this.state.couponApplying}
                      >
                        <span className="material-icons" aria-hidden="true">
                          close
                        </span>
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="billing-coupon__apply"
                    onClick={this.applyCoupon}
                    disabled={!couponTrimmed || this.state.couponApplying}
                  >
                    {this.state.couponApplying ? "Applying…" : "Apply"}
                  </button>
                </div>
                {this.state.showCouponError && (
                  <p className="billing-coupon__error" role="alert">
                    {this.state.couponError}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        <Row className="plans-cards-row plans-cards-row--billing justify-content-center">
          {this.state.products.map((plan) => {
            const isPopular = isPopularPlan(plan);
            const monthsInCycle = getMonthsInCycle(plan.billingCycle);
            const monthlyEquivalent =
              monthsInCycle && monthsInCycle > 1
                ? Math.round(plan.cost / monthsInCycle)
                : null;
            const badgeVariant = getBadgeVariant(plan.name);

            return (
              <Col
                md={{ size: 4 }}
                lg={{ size: 4 }}
                key={plan.id}
                className={
                  isPopular ? "plan-card-col plan-card-col--popular" : "plan-card-col"
                }
              >
                <Card
                  small
                  className={`plan-card${isPopular ? " plan-card--popular" : ""}`}
                >
                  {isPopular && (
                    <span className="plan-card__best-value">Best value</span>
                  )}
                  <CardHeader className="plan-card__header">
                    <h5 className="plan-card__title">
                      {formatPlanTitle(plan.name)}
                    </h5>
                    {this.checkDiscount(plan)}
                    <span className="plan-card__interval">
                      {this.plan(plan.billingCycle).interval}
                    </span>
                    {monthlyEquivalent != null && (
                      <span className="plan-card__price-equivalent">
                        ~${monthlyEquivalent}/mo
                      </span>
                    )}
                    {plan.marketingPhrase && (
                      <span
                        className={`plan-card__badge plan-card__badge--${badgeVariant}`}
                      >
                        {plan.marketingPhrase}
                      </span>
                    )}
                  </CardHeader>
                  <div className="plan-card__description">{plan.description}</div>
                  <button
                    type="button"
                    className={`plan-card__cta${
                      isPopular
                        ? " plan-card__cta--primary"
                        : " plan-card__cta--ghost"
                    }`}
                    onClick={() =>
                      this.props.changePlan(
                        plan.id,
                        this.getCouponForPlan(plan.id),
                        this.getDiscountedCostForPlan(plan)
                      )
                    }
                  >
                    {this.props.actionText}
                  </button>
                </Card>
              </Col>
            );
          })}
        </Row>
      </>
    );
  }
}
